import {
  faImage,
  faPaperPlane,
  faQuoteLeft,
} from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  FamilyChildProfile,
  FamilyParentProfile,
  QuestInstance,
  Typography,
  TextButton,
  JoonUIColor,
  SPACING,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useNavigate } from "react-router-dom"

import { useImageModalStore } from "./ImageModal"
import { useQuestModalStore } from "./useQuestModalStore"
import { UserInitial } from "./UserInitial"
import { useSBStore } from "../../../components/sendbird/useSBStore"
import { completeQuestStatuses } from "../../../constants"
import { ClientTab } from "../ClientTabNav"
import useCurrentClient from "../hooks/useCurrentClient"

export const UserQuestStatus = ({
  profile,
  questGroup,
}: {
  profile: FamilyParentProfile | FamilyChildProfile
  questGroup: QuestInstance[] | null
}) => {
  const quest = questGroup?.find((quest) => quest.user_id === profile.user.id)
  const { onOpen: openImageModal } = useImageModalStore()
  const navigate = useNavigate()
  const { currentCoachingGroup } = useCurrentClient()
  const { onClose: closeQuestModal } = useQuestModalStore()
  const { setInputText } = useSBStore()

  if (!quest) return null

  const isCompleted = completeQuestStatuses.includes(quest.status)

  const handleSendKudos = () => {
    navigate(`/${currentCoachingGroup?.id}/${ClientTab.MESSAGES}`)
    setInputText(
      `In response to:
Task: ${quest.series.title}${
        quest.completion_notes
          ? `
  Your note: ${quest.completion_notes}`
          : ""
      }
----

`
    )
    closeQuestModal()
  }

  const handleViewPhoto = () => {
    if (!quest.completion_photo_url) return
    openImageModal(quest.completion_photo_url, "Completion photo")
  }

  return (
    <div
      key={profile.user.id}
      className="flex flex-col border border-borderPrimary rounded-md items-center"
    >
      <div className="flex flex-row items-center gap-2 w-full px-4 py-3">
        <UserInitial userProfile={profile} isCompleted={isCompleted} />
        <Typography variant="body">{profile.user.name}</Typography>
        {isCompleted ? (
          <div className="flex flex-row items-center gap-2 justify-between w-full">
            <Typography
              variant="bodyXSmall"
              color={JoonUIColor.text.secondary}
              className="text-nowrap"
            >
              Completed on{" "}
              {dayjs.unix(Number(quest.completion_date)).format("MMM D, YYYY")}{" "}
              at {dayjs.unix(Number(quest.completion_date)).format("h:mm A")}
            </Typography>
            <TextButton
              onClick={handleSendKudos}
              className="ml-auto flex flex-row gap-1 items-center"
              style={{ padding: SPACING.space1 }}
            >
              <FontAwesomeIcon
                icon={faPaperPlane}
                fontSize="12px"
                color={JoonUIColor.icon.accent}
              />
              <Typography
                variant="bodySmall"
                color={JoonUIColor.text.primaryAccent}
                className="text-nowrap"
              >
                Send Kudos!
              </Typography>
            </TextButton>
          </div>
        ) : (
          <Typography
            variant="bodySmall"
            color={JoonUIColor.text.secondary}
            className="text-nowrap"
          >
            Incomplete
          </Typography>
        )}
      </div>
      {quest.completion_notes && (
        <div className="flex flex-row items-center px-4 py-3 gap-2 border-t border-borderPrimary w-full">
          <FontAwesomeIcon
            icon={faQuoteLeft}
            fontSize="16px"
            color={JoonUIColor.icon.neutral}
          />
          <Typography variant="bodySmall">{quest.completion_notes}</Typography>
        </div>
      )}
      {quest.completion_photo_url && (
        <div className="flex flex-row items-center px-4 py-2 gap-2 border-t border-borderPrimary w-full">
          <TextButton
            onClick={handleViewPhoto}
            style={{ padding: SPACING.space1 }}
            className="flex flex-row gap-2 items-center"
          >
            <FontAwesomeIcon
              icon={faImage}
              fontSize="12px"
              color={JoonUIColor.icon.neutral}
            />
            <Typography
              variant="bodySmall"
              color={JoonUIColor.text.primaryAccent}
            >
              View uploaded image
            </Typography>
          </TextButton>
        </div>
      )}
    </div>
  )
}
